import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { Table } from "@/components"
import { MexicoMarketType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import Swal from "sweetalert2"
import { api } from "@/lib/api"
import { useQuery } from "react-query"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: MexicoMarketType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: MexicoMarketType) => void
  handleFormUpdate: (form: MexicoMarketType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const MexicoMarket: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MexicoMarketType>()

  const onSubmit = (data: MexicoMarketType) => {
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: MexicoMarketType) => {
    props.setTitle('Update Mexico Market')
    setValue("id", e.id)
    setValue("name", e.name)
    setValue("portOfEntryId", e.portOfEntryId)
    props.setPopup(true)
  }
  const { data } = useQuery<{ data: MexicoMarketType[] }, Error>(
    ["portofEntry"],
    () => api.MexicoMarketApi.AllPortofEntry(),
  )
  useResetOnClose(props.popup, () => {
    reset({
      id: undefined,
      name: "",
      portOfEntryId: "",
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2 place-items-center">
              <fieldset className="w-full">
                <label className="text-xs dark:text-primary-8 text-light-1">Maxico Market</label>
                <input
                  className={
                    (errors.name ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1  outline-none  dark:text-primary-2 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Maxico Market Name"
                  {...register("name", { required: true })}
                />
              </fieldset>
              <fieldset className="w-full">
                <label className="text-xs dark:text-primary-8 text-light-1">Port of Entry</label>
                {Array.isArray(data?.data) ? (
                  <select
                    {...register("portOfEntryId")}
                    className={
                      (errors.portOfEntryId
                        ? "border-red-400"
                        : "dark:border-secondary-2") +
                      " mt-1 w-full dark:bg-primary-1 bg-white dark:text-primary-2 text-light-2 border-2 text-sm px-3 py-2 rounded outline-none overflow-auto"
                    }
                  >
                    <option value="" hidden>
                      Select Port of Entry
                    </option>
                    {data?.data
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((data: MexicoMarketType) => (
                        <option key={data.id} value={data.id} className="h-12">
                          {data.name}
                        </option>
                      ))}
                  </select>
                ) : (
                  <select className="w-full bg-white dark:bg-primary-1 text-light-2 dark:text-primary-2 border dark:border-zinc-400 text-sm px-3 py-2 rounded drop-shadow outline-none">
                    <option value="" disabled>
                      Port of Entry Not Found
                    </option>
                  </select>
                )}
              </fieldset>
            </div>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-80"}>
                <div className="flex justify-between">
                  <span>{e.name}</span>
                  <div>
                    {e.checked ? (
                      <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                    ) : (
                      <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                    )}
                  </div>
                </div>
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.portOfEntry.name}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
